import axios from 'axios'
import { createFormData, createJsonData } from 'helpers/generalHelpers'
import { HTTP } from 'types'
import {
  APIURL,
  config,
  googleApiKey,
  ICAS_API_KEY,
  ICAS_API_URL,
  getCurrentWorkspace,
} from './services/_utils'

import { services } from './services'

export { services }

const workspace = getCurrentWorkspace()

/* api methods */

/* __________________ PROFILE  __________________ */

export const getUserProfile = () =>
  axios.get(`${APIURL}/v4/profile/`, config({ workspace }))

export const changePassword = services.auth.changePassword

export const updateFormalooProfile = (formData, options = {}) =>
  axios.patch(
    `${APIURL}/v4/profile/`,
    formData,
    config({ ...options, workspace })
  )

export const updateProfile = (formData) =>
  Promise.all([
    updateFormalooProfile(formData),
    services.auth.updateProfileData(formData).then(services.auth.getToken()),
  ])

export const getAuthToken = () =>
  axios.post(`${APIURL}/v2/profiles/profile/get-token/`, null, config())

/* __________________ APP BUILDER  __________________ */
export const getFormPrimaryBoard = (formSlug, params) =>
  axios.get(
    `${APIURL}/v4/forms/${formSlug}/boards/primary/`,
    config({ params, workspace })
  )

export const getAllAppTemplates = (params) =>
  axios.get(`${APIURL}/v3.3/template-boards/`, config({ params }))

export const copyBoardByTemplate = (formSlug, boardSlug) =>
  axios.post(
    `${APIURL}/v4/forms/${formSlug}/boards/${boardSlug}/copy/`,
    createJsonData({}),
    config({ contentType: HTTP.ContentTypes.JSON, workspace })
  )

export const getSharedBoardData = (sharedSlug, params = {}) =>
  axios.get(`${APIURL}/v3.3/shared-boards/${sharedSlug}/`, config({ params }))

/* __________________ GOOGLE FONTS  __________________ */
export const GetGoogleFonts = (params = {}) =>
  axios.get(`https://www.googleapis.com/webfonts/v1/webfonts`, {
    params: { key: googleApiKey, ...params },
  })

/* __________________ GOOGLE SHEET  __________________ */
export const getGoogleLoginUrl = (data = {}) =>
  axios.post(
    `${APIURL}/v2/icas/request-redirect/`,
    createJsonData({ grant_access: true, ...data }),
    config({ contentType: HTTP.ContentTypes.JSON })
  )

export const importFromGoogleSheets = (formData) =>
  axios.post(
    `${APIURL}/v4/form-excel-imports/`,
    createJsonData(formData),
    config({ workspace, contentType: HTTP.ContentTypes.JSON })
  )

/* __________________ SUPPORTED CURRENCIES  __________________ */
export const getSupportedCurrencies = (params) =>
  axios.get(`${APIURL}/v4/currencies/`, config({ params, workspace }))

/* __________________ OTHERS  __________________ */

export const getForm = (formSlug, options = {}) =>
  axios.get(
    `${APIURL}/v4/forms/${formSlug}/`,
    config({ workspace, ...options })
  )

export const updateFieldsPositions = (formSlug, formData) =>
  services.form.edit(formSlug, formData)

export const loadTemplates = (params = {}) =>
  axios.get(`${APIURL}/v3/form-templates/`, config({ params }))

export const loadSingleTemplate = (templateSlug) => getForm(templateSlug)

export const loadAllRows = (params) =>
  axios.get(`${APIURL}/v4/rows/`, config({ params, workspace }))

export const search = (searchTerm) =>
  loadFormAndRow({ search: searchTerm, version: 'v1' })

export const searchOnRows = (searchTerm) => loadAllRows(searchTerm)

export const loadFormAndRow = (params) =>
  axios.get(`${APIURL}/v4/form-and-rows/`, config({ params, workspace }))

export const downloadFormResultsAsFile = (formSlug) =>
  axios.post(`${APIURL}/v2/forms/form/${formSlug}/excel/`, null, config())

export const requestPhoneNumberConfirmation = () =>
  axios.post(
    `${ICAS_API_URL}/v1/profiles/mobile/request-verification/`,
    {},
    config({ apiKey: ICAS_API_KEY })
  )

export const confirmMobilePhone = (body) =>
  axios.post(
    `${ICAS_API_URL}/v1/profiles/mobile/verify/`,
    body,
    config({ apiKey: ICAS_API_KEY })
  )

export const requestEmailConfirmation = () =>
  axios.post(
    `${ICAS_API_URL}/v1/profiles/email/request-verification/`,
    {},
    config({ apiKey: ICAS_API_KEY })
  )

export const confirmEmail = (body) =>
  axios.post(
    `${ICAS_API_URL}/v1/profiles/email/verify/`,
    body,
    config({ apiKey: ICAS_API_KEY })
  )

export const getInvitationList = () =>
  axios.get(`${APIURL}/v2/profiles/invitation/list/`, config())

export const sendInvitation = (formObj) =>
  axios.post(
    `${APIURL}/v2/profiles/invitation/send/`,
    createFormData(formObj),
    config()
  )

export const deleteSentInvitation = (invitationSlug) =>
  axios.delete(
    `${APIURL}/v2/profiles/invitation/${invitationSlug}/remove/`,
    config()
  )
export const getTemplateTagList = (params) =>
  axios.get(`${APIURL}/v3/template-tags/`, config({ params }))

export const getThemeList = (params) =>
  axios.get(`${APIURL}/v2/forms/theme/list/`, config({ params }))

export const login = (formObj) =>
  axios.post(`${APIURL}/v1/auth/login/`, createFormData(formObj), config())
export const signUp = (formObj) =>
  axios.post(
    `${APIURL}/v1/profiles/profile/`,
    createFormData(formObj),
    config()
  )
export const smsResetPassRequest = (formObj) =>
  axios.post(
    `${APIURL}/v1/auth/password/reset/mobile/`,
    createFormData(formObj),
    config()
  )

export const emailResetPassRequest = (formObj) =>
  axios.post(
    `${APIURL}/v1/auth/password/reset/email/`,
    createFormData(formObj),
    config()
  )
export const emailResetPassConfirm = (formObj) =>
  axios.post(
    `${APIURL}/v1/auth/password/reset/email/confirm/`,
    createFormData(formObj),
    config()
  )

export const smsResetPassConfirm = (formObj) =>
  axios.post(
    `${APIURL}/v1/auth/password/reset/mobile/confirm/`,
    createFormData(formObj),
    config()
  )

export const generateGuestUser = () =>
  axios.post(`${APIURL}/v2/profiles/guest/`, null, config())

export const generateApiKey = () =>
  axios.post(`${APIURL}/v2/applications/create-api-token/`, null, config())

export const signUpGuest = (formObj) =>
  axios.patch(
    `${APIURL}/v2/profiles/guest/complete-profile/`,
    createFormData(formObj),
    config()
  )

export const getPlansList = (params = {}) =>
  axios.get(`${APIURL}/v1.0/plans/`, config({ params, workspace }))

export const getIntegrationKeys = (params = {}) =>
  axios.get(`${APIURL}/v2.0/keys/`, config({ params, workspace }))
